import { AlertColor } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppPaymentType } from '../../utils/device';
import { useAppDispatch, useAppSelector } from '../../utils/hooks';
import { printInvoice } from '../../utils/terminal';
import { getBasketProducts, getBasketSummary, getBasketTickets, getInvoiceData } from '../basket/selectors';
import { getAcceptedPaymentTypes } from '../layout/selectors';
import { CreateConfirmPaymentRequestModel, MapToProductOrder, MapToRequestTicket } from './helpers';
import { CreateOrderRequest, OrderStatus, PaymentType } from './models';
import { confirmPayment, getOrder, createOrder } from './operations';

export const useSinglePaymentType = (): readonly [PaymentType | null, boolean, boolean] => {
  const [singlePaymentType, setSinglePaymentType] = useState<PaymentType | null>(null);
  const acceptedPaymentTypes = useAppSelector((state) => getAcceptedPaymentTypes(state.layout));
  const basketSummary = useAppSelector((state) => getBasketSummary(state.basket));

  const displayCreditCardPayment: boolean =
    basketSummary.amount > 0 && acceptedPaymentTypes?.find((x) => x === AppPaymentType.CreditCard) !== undefined;

  const displayCashPayment: boolean =
    acceptedPaymentTypes?.find((x) => x === AppPaymentType.Cash) !== undefined || basketSummary.amount === 0;

  useEffect(() => {
    const calcSinglePayment = calculateSinglePaymentType();
    console.log('Calculated single payment', calcSinglePayment);
    setSinglePaymentType(calcSinglePayment);
  }, [displayCreditCardPayment, displayCashPayment]);

  const calculateSinglePaymentType = () => {
    console.log('calculateSinglePaymentType - acceptedPaymentTypes', acceptedPaymentTypes);
    console.log('calculateSinglePaymentType - displayCashPayment', displayCashPayment);
    console.log('calculateSinglePaymentType - displayCreditCardPayment', displayCashPayment);
    if (displayCashPayment && !displayCreditCardPayment) {
      return PaymentType.CASH;
    } else if (displayCreditCardPayment && !displayCashPayment) {
      return PaymentType.CREDIT_CARD;
    }
    return null;
  };

  return [singlePaymentType, displayCashPayment, displayCreditCardPayment];
};

export const useProcessPayment = (
  paymentType: PaymentType | null
): readonly [(ticketId?: string | null) => Promise<void>, boolean, PaymentError] => {
  const [paymentError, setPaymentError] = useState<PaymentError>({ severity: 'success' } as PaymentError);
  const [processing, setProcessing] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const basketSummary = useAppSelector((state) => getBasketSummary(state.basket));
  const invoiceData = useAppSelector((state) => getInvoiceData(state.basket));
  const basketTickets = useAppSelector((state) => getBasketTickets(state.basket));
  const basketProducts = useAppSelector((state) => getBasketProducts(state.basket));

  const process = async (ticketId?: string | null) => {
    if (processing) {
      return;
    }
    if (paymentType !== null) {
      setProcessing(true);
      const requestTickets = MapToRequestTicket(basketTickets);
      const products = MapToProductOrder(basketProducts);

      try {
        if (!ticketId) {
          ticketId = searchParams.get('ticketId');
        }
        let orderStatus = {} as OrderStatus;

        if (ticketId) {
          console.log('[Processing payment] Getting order for ticketId: ' + ticketId);
          orderStatus = await dispatch(getOrder(ticketId)).unwrap();
        } else {
          console.log('[Processing payment] Creating order');
          const createOrderRequest = {
            tickets: requestTickets,
            invoiceData: invoiceData,
            products: products,
          } as CreateOrderRequest;
          orderStatus = await dispatch(createOrder(createOrderRequest)).unwrap();
        }

        console.log(`[Processing payment] OrderId: ${orderStatus.orderId}, total amount: ${orderStatus.totalAmount}`);

        switch (paymentType) {
          case PaymentType.CASH:
            const result = await dispatch(
              confirmPayment(CreateConfirmPaymentRequestModel(orderStatus, paymentType, basketSummary.amount, null))
            ).unwrap();
            if (result) {
              console.log('Start printing. orderid', orderStatus.orderId);
              const printState = await printInvoice(
                orderStatus.orderId ?? '',
                orderStatus?.tickets ?? [],
                orderStatus?.products ?? [],
                invoiceData?.nip,
                paymentType
              );
              console.log('Print status: ' + printState);
              navigate('/payment_wizard/cash');
            } else {
              throw new Error('Cannot start payment');
            }
            break;
          case PaymentType.CREDIT_CARD:
            navigate('/payment_wizard/terminal');
            break;
          default:
            throw new Error('Cannot handle payment type: ' + paymentType);
        }
      } catch (e) {
        console.error('[PaymentWizard]', JSON.stringify(e));
        setProcessing(false);
        setPaymentError({ message: 'Wystąpił problem z rozpoczęciem płatności', severity: 'error' } as PaymentError);
      }
    } else {
      setPaymentError({ message: 'Wybierz formę płatności', severity: 'warning' } as PaymentError);
    }
  };

  return [process, processing, paymentError];
};

interface PaymentError {
  message: string;
  severity: AlertColor;
}
